import React, { useEffect } from 'react';
import { Box, Loader } from '@wix/design-system';
import { observer } from 'mobx-react-lite';
import { FiltersMap } from '@wix/bex-core';
import { CollectionTable, CollectionTableProps } from '../CollectionTable';
import { useInfiniteScrollTableSyncProps } from './useInfiniteScrollTableSyncProps';
import { InfiniteScrollTableProvider } from '../../providers';
import { getToolbarCollectionFeaturesInitializers } from '../../state/getToolbarCollectionFeaturesInitializers';

export interface InfiniteScrollTableBaseProps<T, F extends FiltersMap>
  extends CollectionTableProps<T, F> {
  rowHeight?: number | ((item: T, index: number) => number);
  estimatedRowHeight?: number;
}

function _InfiniteScrollTableBase<T, F extends FiltersMap = FiltersMap>(
  props: InfiniteScrollTableBaseProps<T, F>,
) {
  const {
    state,
    useNewInfiniteScrollLoader,
    internalScroll,
    multiLevelSorting,
  } = props;
  const { table } = state;
  const { collection } = table;
  const {
    fetchedLastPage,
    query: { limit },
  } = collection;

  const { scrollElement } = useInfiniteScrollTableSyncProps(props);

  useEffect(() => {
    return state.init({
      featuresInitializers: [
        ...getToolbarCollectionFeaturesInitializers(props),
      ],
      hasMultiLevelSorting: multiLevelSorting !== undefined,
    });
  }, []);

  const infiniteScrollProps =
    useNewInfiniteScrollLoader || internalScroll
      ? {}
      : {
          infiniteScroll: true,
          loadMore: collection.fetchNextPageIfNeeded,
          hasMore: !fetchedLastPage,
          itemsPerPage: limit,
          scrollElement,
          loader: (
            <Box backgroundColor="D80" align="center" padding="large">
              <Loader dataHook="infinite-scroll-loader" />
            </Box>
          ),
        };

  return (
    <InfiniteScrollTableProvider value={state}>
      <CollectionTable
        {...props}
        {...infiniteScrollProps}
        useNewInfiniteScrollLoader={useNewInfiniteScrollLoader}
        initialLoad={false}
        state={state}
      />
    </InfiniteScrollTableProvider>
  );
}

export const InfiniteScrollTableBase = observer(_InfiniteScrollTableBase);
